import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import styles from './AIServicePage.module.css';
import { useLang } from '../../context/LanguageContext';

/* ── Geometric SVG Icons ──────────────────────────────── */
const IconPredict = () => (
  <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
    <rect x="1" y="1" width="42" height="42" rx="9" stroke="rgba(0,194,255,0.15)" strokeWidth="1"/>
    <polyline points="7,34 15,20 22,27 31,11 37,18" stroke="#00C2FF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="37" cy="18" r="2.8" fill="#00C2FF"/>
    <line x1="37" y1="24" x2="37" y2="37" stroke="rgba(0,194,255,0.25)" strokeWidth="1" strokeDasharray="2.5 2.5"/>
    <line x1="31" y1="29" x2="31" y2="37" stroke="rgba(0,194,255,0.25)" strokeWidth="1" strokeDasharray="2.5 2.5"/>
    <line x1="22" y1="32" x2="22" y2="37" stroke="rgba(0,194,255,0.25)" strokeWidth="1" strokeDasharray="2.5 2.5"/>
  </svg>
);

const IconAgent = () => (
  <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
    <rect x="1" y="1" width="42" height="42" rx="9" stroke="rgba(0,194,255,0.15)" strokeWidth="1"/>
    <rect x="12" y="10" width="20" height="16" rx="4" stroke="#00C2FF" strokeWidth="1.8"/>
    <circle cx="17" cy="18" r="2" fill="#00C2FF"/>
    <circle cx="27" cy="18" r="2" fill="#00C2FF"/>
    <path d="M 17 22 Q 22 26 27 22" stroke="#00C2FF" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
    <path d="M 22 26 L 22 31" stroke="rgba(0,194,255,0.5)" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M 14 31 Q 22 28 30 31" stroke="#00C2FF" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
    <line x1="8" y1="14" x2="12" y2="17" stroke="rgba(0,194,255,0.35)" strokeWidth="1.2" strokeLinecap="round"/>
    <line x1="36" y1="14" x2="32" y2="17" stroke="rgba(0,194,255,0.35)" strokeWidth="1.2" strokeLinecap="round"/>
  </svg>
);

const IconDomain = () => (
  <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
    <rect x="1" y="1" width="42" height="42" rx="9" stroke="rgba(0,194,255,0.15)" strokeWidth="1"/>
    <rect x="7" y="12" width="12" height="14" rx="2.5" stroke="#00C2FF" strokeWidth="1.8"/>
    <rect x="25" y="12" width="12" height="14" rx="2.5" stroke="rgba(0,194,255,0.4)" strokeWidth="1.8"/>
    <path d="M 19 19 L 25 19" stroke="#00C2FF" strokeWidth="1.5" strokeDasharray="2 2"/>
    <rect x="16" y="30" width="12" height="7" rx="2" stroke="#00C2FF" strokeWidth="1.8"/>
    <line x1="13" y1="26" x2="16" y2="30" stroke="rgba(0,194,255,0.45)" strokeWidth="1.2"/>
    <line x1="31" y1="26" x2="28" y2="30" stroke="rgba(0,194,255,0.45)" strokeWidth="1.2"/>
    <circle cx="13" cy="19" r="1.5" fill="rgba(0,194,255,0.6)"/>
    <circle cx="31" cy="19" r="1.5" fill="rgba(0,194,255,0.6)"/>
  </svg>
);

const IconMultiAgent = () => (
  <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
    <rect x="1" y="1" width="42" height="42" rx="9" stroke="rgba(0,194,255,0.15)" strokeWidth="1"/>
    <circle cx="22" cy="22" r="4" fill="#00C2FF"/>
    <circle cx="8"  cy="12" r="3.5" stroke="#00C2FF" strokeWidth="1.8"/>
    <circle cx="36" cy="12" r="3.5" stroke="#00C2FF" strokeWidth="1.8"/>
    <circle cx="8"  cy="32" r="3.5" stroke="#00C2FF" strokeWidth="1.8"/>
    <circle cx="36" cy="32" r="3.5" stroke="#00C2FF" strokeWidth="1.8"/>
    <line x1="11.5" y1="14.5" x2="18.5" y2="19" stroke="rgba(0,194,255,0.4)" strokeWidth="1.2"/>
    <line x1="32.5" y1="14.5" x2="25.5" y2="19" stroke="rgba(0,194,255,0.4)" strokeWidth="1.2"/>
    <line x1="11.5" y1="29.5" x2="18.5" y2="25" stroke="rgba(0,194,255,0.4)" strokeWidth="1.2"/>
    <line x1="32.5" y1="29.5" x2="25.5" y2="25" stroke="rgba(0,194,255,0.4)" strokeWidth="1.2"/>
    <circle cx="22" cy="22" r="1.8" fill="#0A0A0F"/>
  </svg>
);

const IconVision = () => (
  <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
    <rect x="1" y="1" width="42" height="42" rx="9" stroke="rgba(0,194,255,0.15)" strokeWidth="1"/>
    <rect x="6" y="6" width="9" height="9" rx="1.5" stroke="#00C2FF" strokeWidth="1.8"/>
    <rect x="29" y="6" width="9" height="9" rx="1.5" stroke="#00C2FF" strokeWidth="1.8"/>
    <rect x="6" y="29" width="9" height="9" rx="1.5" stroke="#00C2FF" strokeWidth="1.8"/>
    <rect x="29" y="29" width="9" height="9" rx="1.5" stroke="#00C2FF" strokeWidth="1.8"/>
    <circle cx="22" cy="22" r="8" stroke="rgba(0,194,255,0.35)" strokeWidth="1.5" strokeDasharray="3 2.5"/>
    <circle cx="22" cy="22" r="4" stroke="rgba(0,194,255,0.6)" strokeWidth="1.5"/>
    <circle cx="22" cy="22" r="2" fill="#00C2FF"/>
  </svg>
);

const IconIntegration = () => (
  <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
    <rect x="1" y="1" width="42" height="42" rx="9" stroke="rgba(0,194,255,0.15)" strokeWidth="1"/>
    <rect x="5" y="14" width="15" height="16" rx="3" stroke="rgba(0,194,255,0.35)" strokeWidth="1.8"/>
    <rect x="24" y="14" width="15" height="16" rx="3" stroke="#00C2FF" strokeWidth="1.8"/>
    <path d="M 20 22 L 24 22" stroke="#00C2FF" strokeWidth="2" strokeLinecap="round"/>
    <path d="M 31 8 L 31 14 M 31 30 L 31 36" stroke="rgba(0,194,255,0.45)" strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="31" cy="22" r="2.5" fill="#00C2FF"/>
    <path d="M 12 8 L 12 14 M 12 30 L 12 36" stroke="rgba(0,194,255,0.2)" strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="12" cy="22" r="2" fill="rgba(0,194,255,0.4)"/>
  </svg>
);

/* ── Data ─────────────────────────────────────────────── */
const deliverables = [
  { icon: <IconPredict />,     label: 'Modèles prédictifs',                desc: "Anticipez les pannes, demandes et comportements avant qu'ils surviennent." },
  { icon: <IconAgent />,       label: 'Agents IA',                         desc: "Assistants autonomes qui dialoguent, recherchent et soutiennent vos équipes." },
  { icon: <IconDomain />,      label: 'Modèles spécialisés par domaine',   desc: "Une IA entraînée sur vos données — pas un modèle générique." },
  { icon: <IconMultiAgent />,  label: 'Systèmes multi-agents',             desc: "Plusieurs agents qui collaborent pour résoudre des tâches complexes de bout en bout." },
  { icon: <IconVision />,      label: 'Vision par ordinateur',             desc: "Détection d'objets, surveillance qualité et analyse vidéo en temps réel." },
  { icon: <IconIntegration />, label: "Intégration IA dans l'existant",    desc: "Ajouter des capacités IA à un logiciel déjà en production, sans tout reconstruire." },
];

const situations = [
  {
    num: '01',
    title: "Vous avez des données mais vous n'en tirez pas de valeur",
    desc: "Logs, transactions, capteurs, historiques clients — ces données contiennent des signaux que l'IA peut exploiter directement.",
  },
  {
    num: '02',
    title: 'Vous avez des tâches répétitives à haut volume',
    desc: "Traitement de documents, tri de demandes, catégorisation, résumés — autant de tâches où l'IA libère du temps humain.",
  },
  {
    num: '03',
    title: 'Vous avez besoin de décisions rapides sur des données complexes',
    desc: "Scoring de risque, recommandations, détection d'anomalies — des décisions que l'humain ne peut pas traiter à cette vitesse.",
  },
];

const differentiators = [
  { title: "Pas un wrapper ChatGPT", desc: "Des systèmes construits pour votre contexte précis — architecture, données, contraintes et objectifs qui vous appartiennent." },
  { title: "Expérience sur des projets IA en production", desc: "Pas des démos. Des modèles et des agents qui tournent dans de vrais environnements, avec de vraies contraintes." },
  { title: "De l'idée au déploiement", desc: "Un seul interlocuteur de la phase de découverte jusqu'au monitoring en production. Pas de handoff, pas de perte d'information." },
];

const techs = ['Agents IA', 'Chatbots sur mesure', 'Traitement du langage', 'Vision par ordinateur', 'Automatisation intelligente', 'Intégration API IA', 'RAG & bases de connaissances', 'Déploiement cloud'];


/* ── Animations ─────────────────────────────────────────── */
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.09, delayChildren: 0.05 } } };
const fadeUp  = { hidden: { opacity: 0, y: 28, filter: 'blur(4px)' }, visible: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } } };

/* ── Page ──────────────────────────────────────────────── */
export default function AIServicePage() {
  const delivRef = useRef(null);
  const sitRef   = useRef(null);
  const diffRef  = useRef(null);
  const delivInView = useInView(delivRef, { once: true, margin: '-60px' });
  const sitInView   = useInView(sitRef,   { once: true, margin: '-60px' });
  const diffInView  = useInView(diffRef,  { once: true, margin: '-60px' });
  const { lang } = useLang();

  const ui = {
    fr: {
      breadcrumb: 'Solutions IA',
      heroT1:     'Solutions IA sur mesure pour',
      heroHL1:    'automatiser',
      heroT2:     ',',
      heroHL2:    'prédire',
      heroT3:     'et mieux décider.',
      heroSub:    "J'aide les entreprises à intégrer l'intelligence artificielle dans leurs processus réels — pas des démos, des systèmes qui tournent en production.",
      ctaPrimary: "Discuter de mon cas d'usage",
      ctaGhost:   'Voir les livrables',
      delivEy:    'Ce que je construis',
      delivT1:    'Six capacités IA,',
      delivT2:    'un seul interlocuteur.',
      sitEy:      'Pour qui ?',
      sitT1:      'Trois situations',
      sitT2:      'qui appellent une solution IA.',
      diffEy:     'Ce qui me différencie',
      diffT1:     "De l'IA utile,",
      diffT2:     "pas de l'IA décorative.",
      techLabel:  'Stack technique',
    },
    en: {
      breadcrumb: 'AI Solutions',
      heroT1:     'Custom AI solutions to',
      heroHL1:    'automate',
      heroT2:     ',',
      heroHL2:    'predict',
      heroT3:     'and make better decisions.',
      heroSub:    "I help businesses integrate AI into their real processes — not demos, systems that run in production.",
      ctaPrimary: 'Discuss my use case',
      ctaGhost:   'See deliverables',
      delivEy:    'What I build',
      delivT1:    'Six AI capabilities,',
      delivT2:    'one single point of contact.',
      sitEy:      'For whom?',
      sitT1:      'Three situations',
      sitT2:      'that call for an AI solution.',
      diffEy:     'What sets me apart',
      diffT1:     'Useful AI,',
      diffT2:     'not decorative AI.',
      techLabel:  'Tech stack',
    },
  }[lang];

  return (
    <div className={styles.page}>

      {/* ── HERO ── */}
      <section className={styles.hero}>
        <div className={styles.heroBg}>
          <div className={styles.gridLines} />
          <div className={styles.glow} />
        </div>
        <div className={styles.heroInner}>
          <motion.div className={styles.breadcrumb}
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            <Link to="/services" className={styles.breadcrumbLink}>Services</Link>
            <span className={styles.sep}>/</span>
            <span>{ui.breadcrumb}</span>
          </motion.div>

          <motion.h1 className={styles.heroTitle}
            initial={{ opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}>
            {ui.heroT1}{' '}
            <span className={styles.accent}>{ui.heroHL1}</span>{ui.heroT2}{' '}
            <span className={styles.accent}>{ui.heroHL2}</span>{' '}
            {ui.heroT3}
          </motion.h1>

          <motion.p className={styles.heroSub}
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut', delay: 0.24 }}>
            {ui.heroSub}
          </motion.p>

          <motion.div className={styles.heroCtas}
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: 'easeOut', delay: 0.36 }}>
            <Link to="/contact" className={styles.ctaPrimary}>
              {ui.ctaPrimary}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
            </Link>
            <a href="#livrables" className={styles.ctaGhost}>{ui.ctaGhost}</a>
          </motion.div>
        </div>
      </section>

      {/* ── LIVRABLES ── */}
      <section id="livrables" className={styles.section} ref={delivRef}>
        <div className={styles.container}>
          <div className={styles.sectionHead} data-aos="fade-up">
            <p className={styles.eyebrow}>{ui.delivEy}</p>
            <h2 className={styles.sectionTitle}>{ui.delivT1}<br />{ui.delivT2}</h2>
          </div>
          <motion.div className={styles.delivGrid} variants={stagger} initial="hidden" animate={delivInView ? 'visible' : 'hidden'}>
            {deliverables.map((d) => (
              <motion.div key={d.label.fr} className={styles.delivCard} variants={fadeUp}>
                <div className={styles.delivIcon}>{d.icon}</div>
                <div>
                  <p className={styles.delivLabel}>{d.label[lang]}</p>
                  <p className={styles.delivDesc}>{d.desc[lang]}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── POUR QUI ── */}
      <section className={`${styles.section} ${styles.sectionAlt}`} ref={sitRef}>
        <div className={styles.container}>
          <div className={styles.sectionHead} data-aos="fade-up">
            <p className={styles.eyebrow}>{ui.sitEy}</p>
            <h2 className={styles.sectionTitle}>{ui.sitT1}<br />{ui.sitT2}</h2>
          </div>
          <motion.div className={styles.sitGrid} variants={stagger} initial="hidden" animate={sitInView ? 'visible' : 'hidden'}>
            {situations.map((s) => (
              <motion.div key={s.num} className={styles.sitCard} variants={fadeUp}>
                <span className={styles.sitNum}>{s.num}</span>
                <h3 className={styles.sitTitle}>{s.title[lang]}</h3>
                <p className={styles.sitDesc}>{s.desc[lang]}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── DIFFÉRENCIATEURS ── */}
      <section className={styles.section} ref={diffRef}>
        <div className={styles.container}>
          <div className={styles.sectionHead} data-aos="fade-up">
            <p className={styles.eyebrow}>{ui.diffEy}</p>
            <h2 className={styles.sectionTitle}>{ui.diffT1}<br />{ui.diffT2}</h2>
          </div>
          <motion.div className={styles.diffList} variants={stagger} initial="hidden" animate={diffInView ? 'visible' : 'hidden'}>
            {differentiators.map((d) => (
              <motion.div key={d.title.fr} className={styles.diffItem} variants={fadeUp}>
                <span className={styles.diffArrow}>→</span>
                <div>
                  <p className={styles.diffTitle}>{d.title[lang]}</p>
                  <p className={styles.diffDesc}>{d.desc[lang]}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── TECHNOLOGIES ── */}
      <section className={`${styles.section} ${styles.sectionAlt}`} data-aos="fade-up">
        <div className={styles.container}>
          <div className={styles.techRow}>
            <p className={styles.techLabel}>{ui.techLabel}</p>
            <div className={styles.techBadges}>
              {techs.map((t) => <span key={t} className={styles.techBadge}>{t}</span>)}
            </div>
          </div>
        </div>
      </section>


    </div>
  );
}
